import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { useEffect, useId, useState } from 'react';
import MapView from '@arcgis/core/views/MapView';
import FeatureEffect from '@arcgis/core/layers/support/FeatureEffect';
import type Graphic from '@arcgis/core/Graphic.js';
import type FeatureLayerView from '@arcgis/core/views/layers/FeatureLayerView.js';
import type { ClickEvent, PointerDownEvent as ViewPointerDownEvent, PointerMoveEvent as ViewPointerMoveEvent } from '@arcgis/core/views/input/types.js';
import type { GraphicHit } from '@arcgis/core/views/types.js';
import * as promiseUtils from '@arcgis/core/core/promiseUtils';
import { DeviceType, useDeviceDetection } from '@repo/utils';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';

type RemovableHandle = { remove(): void };
type ViewHitTestResponse = Awaited<ReturnType<MapView['hitTest']>>;

/**
 * Options for configuring hover interaction on a single ArcGIS FeatureLayer in a MapView.
 *
 * @param showPointer - Show a CSS pointer cursor on the map while hovering over a feature.
 * @param highlightSymbol - A custom ArcGIS symbol to use as the highlight indicator on the hovered feature.
 * @param glowOnHover - Whether to highlight the feature with a bloom glow effect on hover.
 * @param glowStripFill - When glowing a filled-polygon layer, strips the fill from the hovered feature so only the outline glows under the bloom — the layer's original renderer is restored when the hover ends. Only meaningful together with `glowOnHover`.
 */
interface FeatureHoverOptions {
  showPointer?: boolean;
  highlightSymbol?: SimpleLineSymbol | SimpleFillSymbol;
  highlightOnHover?: boolean;
  glowOnHover?: boolean;
  /**
   * When glowing a filled-polygon layer, strip the fill from the hovered
   * feature so only the outline glows under the bloom (rather than the
   * translucent fill washing out). The layer's original renderer is restored
   * when the hover ends. Only meaningful together with `glowOnHover`. (PRM-495)
   */
  glowStripFill?: boolean;
}

/**
 * Clones a renderer and zeroes the alpha of any `simple-fill` symbols
 * (default symbol, unique-value infos, class-break infos) while leaving their
 * outlines intact — so a bloom glow lights only the outline, not the fill.
 */
const stripFillFromRenderer = (renderer: any): any => {
  const clone = renderer?.clone ? renderer.clone() : renderer;
  const stripSymbol = (symbol: any) => {
    if (symbol?.type === 'simple-fill') symbol.color = [0, 0, 0, 0];
  };
  if (clone?.symbol) stripSymbol(clone.symbol); // SimpleRenderer
  if (clone?.defaultSymbol) stripSymbol(clone.defaultSymbol); // UniqueValueRenderer
  clone?.uniqueValueInfos?.forEach((info: any) => stripSymbol(info.symbol));
  clone?.classBreakInfos?.forEach((info: any) => stripSymbol(info.symbol));
  return clone;
};

interface InternalMapView extends MapView {
  cursor: 'default' | 'pointer';
}


const cursorRefCounter: {
  count: number,
  hovering: { [key: string]: boolean }
} = { count: 0, hovering: {} };

const isAnyHovering = () => {
  const values = Object.values(cursorRefCounter.hovering);
  return values.some((i) => i);
};

// https://developers.arcgis.com/javascript/latest/sample-code/sandbox/?sample=view-hittest
/**
 * Returns an array of [hovered graphics, isHovering boolean] for a list of ArcGIS FeatureLayers in a MapView.
 *
 * Sets up pointer-move (or pointer-down on non-desktop) hit testing with configurable
 * options for highlighting, glow, and pointer cursor.
 *
 * @param view - The ArcGIS MapView instance (may be null/undefined).
 * @param featureLayers - The FeatureLayers to test hits against.
 * @param options - Configuration controlling highlight, glow, and pointer behavior.
 * @param clickCallback - Optional callback invoked when the user clicks a feature.
 * @returns [hoveredGraphicHits, isHovering] React state pair tracking the currently hovered graphic hits and whether any feature is being hovered.
 */
export const useFeatureHover = (
  view: InternalMapView | MapView | undefined | null,
  featureLayers: (FeatureLayer | undefined)[],
  options: FeatureHoverOptions = { showPointer: true, highlightOnHover: true, glowOnHover: false },
  clickCallback?: (graphics: GraphicHit[], isAggregate?: boolean, event?: ClickEvent) => void
): [GraphicHit[], boolean] => {
  const id = useId();
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const [hoveredResult, setHoveredResult] = useState<GraphicHit[]>([]);
  const device = useDeviceDetection();

  useEffect(() => {
    cursorRefCounter.count += 1;
  }, []);

  useEffect(() => {
    if (view) {
      const clickHandle = view.on('click', async (event: ClickEvent) => {
        // Right-click is reserved for the context menu (state/country/property
        // directions, etc.) — it must never trigger feature selection/navigation.
        if (event.button === 2) return;
        const hitResults = await view.hitTest(event, { include: featureLayers.filter((i): i is FeatureLayer => Boolean(i?.loaded)) });
        const graphicResults = hitResults.results.filter((result): result is GraphicHit => result.type === 'graphic');
        if (graphicResults && graphicResults.some((i) => i.graphic.isAggregate) && clickCallback) {
          // this handles if you click an aggregate graphic like a cluster pin
          const graphic = graphicResults[0]?.graphic;
          view?.whenLayerView(graphic?.layer as FeatureLayer).then(async (featureLayerView) => {
            const typedFeatureLayerView = featureLayerView as FeatureLayerView;
            const query = typedFeatureLayerView.createQuery();
            const objectId = graphic?.getObjectId();
            if (objectId) {
              query.aggregateIds = [objectId];

              const { features } = await typedFeatureLayerView.queryFeatures(query);
              if (features.length > 0) {
                const newClickedResult = features.map((i: Graphic) => {
                  return {
                    type: 'graphic',
                    graphic: i,
                    layer: i.layer,
                    mapPoint: graphicResults[0]?.mapPoint
                  } as GraphicHit;
                });
                clickCallback(newClickedResult, true, event);
              }
            }
          });
        } else if (clickCallback) clickCallback(graphicResults, false, event);
      });
      return () => {
        if (clickHandle) clickHandle.remove();
      };
    }
    // if (view && device !== DeviceType.desktop && clickCallback) {
    //   const clickHandle = view.on('click', async (event) => {
    //     const hitResults = await view.hitTest(event, { include: featureLayers.filter(i => i?.loaded) as FeatureLayer[] });
    //     if (clickCallback) clickCallback(hitResults.results as GraphicHit[]);
    //   });
    //   return () => {
    //     if (clickHandle) clickHandle.remove();
    //   };
    // }
  }, [device, view, clickCallback, featureLayers]);

  useEffect(() => {
    if (options.glowOnHover && featureLayers) {
      featureLayers.forEach((fl) => {
        if (fl) {
          fl.featureEffect = undefined;
        }
      });
    }
  }, [options.glowOnHover, featureLayers]);

  useEffect(() => {
    let viewMoveHandle: RemovableHandle | undefined;
    let highlight: RemovableHandle | undefined | null;

    // Tracks layers whose fill we stripped for the glow, so we can restore the
    // original (filled) renderer when the hover leaves / the effect tears down.
    // Kept local to this effect run — this hook is the only owner of the swap,
    // so there's no cross-system race over the layer's renderer. (PRM-495)
    const strippedRenderers = new Map<FeatureLayer, any>();

    const restoreStrippedFills = () => {
      strippedRenderers.forEach((original, layer) => { layer.renderer = original; });
      strippedRenderers.clear();
    };

    const processHitTest = (response: ViewHitTestResponse) => {
      const results = response.results.filter((result): result is GraphicHit => result.type === 'graphic');
      if (results.length) {
        highlight?.remove();
        highlight = null;
        if (results.length === 0) return;
        const graphic = results[0]?.graphic;
        if (graphic) {
          // if graphic is different from hoveredResult then we continue else return
          if (!hoveredResult.some((i) => i.graphic.getObjectId() === graphic.getObjectId())) {
            setHoveredResult((prevResults) => {
              const prevOID = prevResults.map((i) => i.graphic.getObjectId()).sort();
              const currentOID = results.map((i) => i.graphic.getObjectId()).sort();
              return JSON.stringify(prevOID) !== JSON.stringify(currentOID) ? results : prevResults;
            });

            if (options.highlightOnHover) {
              view?.whenLayerView(graphic.layer as FeatureLayer).then((featureLayerView) => {
                highlight = (featureLayerView as FeatureLayerView)?.highlight(graphic) as RemovableHandle;
              });
            }

            if (options.glowOnHover && !graphic.isAggregate) {
              const objectId = graphic.getObjectId();
              if (typeof objectId === 'number') {
                const layer = graphic.layer as FeatureLayer;
                // Restore any previously-stripped layer before glowing the new
                // feature, so at most one feature's fill is stripped at a time.
                restoreStrippedFills();
                if (options.glowStripFill && layer.renderer) {
                  strippedRenderers.set(layer, layer.renderer);
                  layer.renderer = stripFillFromRenderer(layer.renderer);
                }
                layer.featureEffect = new FeatureEffect({
                  filter: { objectIds: [objectId] },
                  includedEffect: 'bloom(2, 0.5px ,0)'
                });
              }
            }

            if (options.showPointer) {
              cursorRefCounter.hovering[id] = true;
            }
          }
        }
      } else {
        highlight?.remove();
        highlight = null;

        // Hover left every feature — restore any fill we stripped for the glow.
        restoreStrippedFills();

        if (setIsHovering) setIsHovering(false);
        if (hoveredResult.length > 0) setHoveredResult([]);
        if (options.showPointer) cursorRefCounter.hovering[id] = false;
      }
      if (view) {
        (view as InternalMapView).cursor = isAnyHovering() ? 'pointer' : 'default';
      }
    };

    if (view && featureLayers.every((i) => i)) {
      const eventHandler = promiseUtils.debounce(async (event: ViewPointerMoveEvent | ViewPointerDownEvent) => {
        return view.hitTest(event, { include: featureLayers.filter((i): i is FeatureLayer => Boolean(i?.loaded)) })
          .then(processHitTest);
      });

      viewMoveHandle = device === DeviceType.desktop
        ? view.on('pointer-move', (event: ViewPointerMoveEvent) => eventHandler(event).catch((error) => {
          if (!promiseUtils.isAbortError(error)) {
            throw error;
          }
        }))
        : view.on('pointer-down', promiseUtils.debounce(eventHandler));
    }

    return () => {
      viewMoveHandle?.remove();
      // Restore any fill-stripped renderer so we never leave a layer in the
      // stripped state after this effect (re)runs or the component unmounts.
      restoreStrippedFills();
    };
  }, [
    device,
    featureLayers,
    hoveredResult,
    id,
    options.glowOnHover,
    options.glowStripFill,
    options.highlightOnHover,
    options.showPointer,
    view
  ]);
  return [hoveredResult, isHovering];
};
